import { useState, type FormEvent } from "react";
import Reveal from "@/components/feature/Reveal";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { comments } from "@/mocks/homeData";

export default function CommentSection() {
  const [liked, setLiked] = useState<Record<number, boolean>>({});
  const [expanded, setExpanded] = useState(false);
  const [notice, setNotice] = useState("");

  const visible = expanded ? comments : comments.slice(0, 4);

  const toggleLike = (index: number) => {
    setLiked((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNotice("这是演示页面，评论暂不对外提交，感谢你的参与。");
  };

  return (
    <section id="discussion" className="w-full bg-background-100">
      <div className="mx-auto max-w-[1400px] px-4 py-14 md:px-8 md:py-24">
        <Reveal>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading
              index="06"
              eyebrow="Discussion"
              title="讨论区：这里的声音更热闹一些"
              desc="看完整条时间线，你可能也有想说的。带上你的判断，也记得给不同的看法留一点位置。"
            />
            <div className="flex flex-wrap gap-6 border-t border-foreground-900/10 pt-4 lg:border-0 lg:pt-0">
              <div>
                <p className="font-heading text-2xl font-bold text-foreground-950">1,248</p>
                <p className="mt-1 text-xs text-foreground-500">条讨论</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-foreground-950">3,096</p>
                <p className="mt-1 text-xs text-foreground-500">人参与</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-accent-600">热门</p>
                <p className="mt-1 text-xs text-foreground-500">正在升温</p>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-8 md:mt-14 lg:grid-cols-12 lg:gap-10">
          <Reveal className="lg:col-span-8">
            <div className="flex flex-col">
              {visible.map((comment, index) => (
                <Reveal key={comment.name} delay={index * 60}>
                  <article className="border-t border-foreground-900/10 py-6 md:py-7">
                    <div className="flex gap-4">
                      <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-secondary-100 font-heading text-base font-bold text-secondary-900">
                        {comment.initials}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-heading text-sm font-bold text-foreground-950">
                            {comment.name}
                          </span>
                          {comment.hot ? (
                            <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] text-accent-900">
                              <span className="flex h-3 w-3 items-center justify-center">
                                <i className="ri-fire-line text-[12px]" />
                              </span>
                              热门
                            </span>
                          ) : null}
                          <span className="text-xs text-foreground-500">{comment.time}</span>
                        </div>
                        <p className="mt-2 text-sm leading-loose text-foreground-700">{comment.content}</p>

                        <div className="mt-3 flex items-center gap-5">
                          <button
                            type="button"
                            onClick={() => toggleLike(index)}
                            className={[
                              "flex cursor-pointer items-center gap-1.5 text-xs transition-colors",
                              liked[index] ? "text-accent-600" : "text-foreground-500 hover:text-foreground-800",
                            ].join(" ")}
                          >
                            <span className="flex h-4 w-4 items-center justify-center">
                              <i className={liked[index] ? "ri-heart-fill" : "ri-heart-line"} />
                            </span>
                            {comment.likes + (liked[index] ? 1 : 0)}
                          </button>
                          <button
                            type="button"
                            className="flex cursor-pointer items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-foreground-800"
                          >
                            <span className="flex h-4 w-4 items-center justify-center">
                              <i className="ri-chat-1-line" />
                            </span>
                            回复
                          </button>
                        </div>

                        {comment.replies.length > 0 ? (
                          <div className="mt-4 flex flex-col gap-3 border-l-2 border-secondary-200 pl-4">
                            {comment.replies.map((reply) => (
                              <div key={reply.name} className="flex gap-3">
                                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-background-200 font-heading text-xs font-bold text-foreground-700">
                                  {reply.initials}
                                </span>
                                <div>
                                  <span className="font-heading text-xs font-bold text-foreground-900">
                                    {reply.name}
                                  </span>
                                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                                    {reply.content}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}

              {!expanded ? (
                <div className="border-t border-foreground-900/10 pt-6">
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-foreground-900/15 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-foreground-900/30"
                  >
                    展开更多讨论
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-arrow-down-line" />
                    </span>
                  </button>
                </div>
              ) : null}
            </div>
          </Reveal>

          <Reveal className="lg:col-span-4" delay={100}>
            <form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-300/70 bg-background-50 p-6"
            >
              <h4 className="font-heading text-lg font-bold text-foreground-950">写下你的看法</h4>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                理性讨论，不披露他人隐私。带情绪可以，别带人身攻击。
              </p>

              <label className="mt-5 block text-xs text-foreground-600">
                昵称
                <input
                  type="text"
                  name="nickname"
                  placeholder="留个称呼吧"
                  className="mt-2 w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </label>

              <label className="mt-4 block text-xs text-foreground-600">
                内容
                <textarea
                  name="content"
                  rows={4}
                  maxLength={500}
                  placeholder="说说你对这起事件的看法……"
                  className="mt-2 w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-3 py-2 text-sm leading-relaxed text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </label>

              <button
                type="submit"
                className="mt-4 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-send-plane-line" />
                </span>
                发表讨论
              </button>

              {notice ? (
                <p className="mt-3 rounded-md bg-primary-100 px-3 py-2 text-xs text-primary-900">{notice}</p>
              ) : null}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}